/**
 * LangSmith integration for LLM observability
 * Tracks OpenAI Assistant API calls
 */

interface LangSmithRun {
  name: string
  run_type: 'llm'
  inputs: {
    jobId: string
    title: string
    company: string
    description: string
  }
  outputs: {
    score: number | null
    verdict: string | null
    reasoning: string | null
  }
  metadata?: {
    latency?: number
    tokensUsed?: number
    cost?: number
    timestamp?: string
  }
}

let langsmithApiKey: string | null = null
let langsmithProject: string | null = null
let langsmithEndpoint: string = 'https://eu.api.smith.langchain.com'
let isEnabled = false

export async function initLangSmith() {
  // Get from environment variables
  const apiKey = import.meta.env.VITE_LANGSMITH_API_KEY || ''
  const project = import.meta.env.VITE_LANGSMITH_PROJECT || ''
  const endpoint = import.meta.env.VITE_LANGSMITH_ENDPOINT || 'https://eu.api.smith.langchain.com'

  if (apiKey) {
    langsmithApiKey = apiKey
    langsmithProject = project || null
    langsmithEndpoint = endpoint
    isEnabled = true
    console.log('[LANGSMITH] ✅ Initialized successfully', {
      endpoint,
      project: project || 'default'
    })
  } else if (import.meta.env.DEV) {
    console.warn(
      '[LANGSMITH] API key not configured, skipping initialization. Set VITE_LANGSMITH_API_KEY in packages/web/.env.local',
    )
  } else {
    console.debug(
      '[LANGSMITH] API key not configured at build time — job relevance traces disabled. Set VITE_LANGSMITH_* in deploy env (see docs/technical/HIR-384-langsmith-optimization-tracing.md)',
    )
  }
}

export async function trackOpenAICall(
  runName: string,
  input: LangSmithRun['inputs'],
  output: LangSmithRun['outputs'],
  metadata?: LangSmithRun['metadata']
) {
  if (!isEnabled || !langsmithApiKey) {
    console.debug('[LANGSMITH] Skipping track - not enabled or no API key')
    return
  }

  try {
    const startTime = Math.floor(Date.now() / 1000) // Unix timestamp in seconds
    const endTime = startTime + Math.floor((metadata?.latency || 0) / 1000)
    
    // LangSmith API format for manual instrumentation
    const run: LangSmithRun = {
      name: runName,
      run_type: 'llm',
      inputs: {
        messages: [
          {
            role: 'user',
            content: `Job Relevance Analysis: ${input.title} at ${input.company}\n\nDescription: ${input.description}`
          }
        ],
        jobId: input.jobId,
        title: input.title,
        company: input.company
      },
      outputs: output.score !== null ? {
        score: output.score,
        verdict: output.verdict,
        reasoning: output.reasoning
      } : {
        error: output.reasoning || 'Unknown error'
      },
      metadata: {
        ...metadata,
        jobId: input.jobId
      },
      start_time: startTime,
      end_time: endTime,
      status: output.score !== null ? 'success' : 'error'
    }

    // Add project_name to query parameter if specified
    let url = `${langsmithEndpoint}/api/v1/runs`
    if (langsmithProject) {
      url += `?project_name=${encodeURIComponent(langsmithProject)}`
    }

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': langsmithApiKey,
      },
      body: JSON.stringify(run),
    })

    if (!response.ok) {
      const errorText = await response.text().catch(() => 'Unknown error')
      console.error('[LANGSMITH] Failed to track run:', response.status, response.statusText, errorText)
    } else {
      const result = await response.json().catch(() => null)
      console.debug('[LANGSMITH] ✅ Tracked OpenAI call:', runName, result?.id ? `(run_id: ${result.id})` : '')
    }
  } catch (error) {
    console.error('[LANGSMITH] Error tracking OpenAI call:', error)
  }
}
